import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import adminService from '../../services/adminService';

const DURATIONS = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: 'custom', label: 'Custom date' },
  { value: 'indefinite', label: 'Indefinite (until manually lifted)' },
];

/**
 * Suspend/Ban confirmation dialog. `users` is an array of { id, name } —
 * one entry for row actions, many for bulk suspend. The balance panel is
 * only shown for a single user (bulk balance lookups would be noisy).
 *
 * onConfirm({ status, reason, suspendedUntil }) does the API calls.
 */
const UserStatusDialog = ({ open, users, initialMode = 'suspended', onClose, onConfirm }) => {
  const [mode, setMode] = useState(initialMode);
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('30');
  const [customDate, setCustomDate] = useState('');
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const singleUser = users.length === 1 ? users[0] : null;
  const singleUserId = singleUser?.id;

  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setReason('');
    setDuration('30');
    setCustomDate('');
    setError(null);
    setBalance(null);
    if (singleUserId) {
      setBalanceLoading(true);
      adminService.getUserBalance(singleUserId)
        .then(setBalance)
        .catch(() => setBalance({ withdrawable_balance: 0 }))
        .finally(() => setBalanceLoading(false));
    }
  }, [open, initialMode, singleUserId]);

  const suspendedUntil = useMemo(() => {
    if (mode !== 'suspended' || duration === 'indefinite') return null;
    if (duration === 'custom') {
      return customDate ? new Date(customDate).toISOString() : null;
    }
    return new Date(Date.now() + Number(duration) * 24 * 3600 * 1000).toISOString();
  }, [mode, duration, customDate]);

  const canConfirm = reason.trim().length >= 3
    && !submitting
    && !(mode === 'suspended' && duration === 'custom' && !suspendedUntil);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm({ status: mode, reason: reason.trim(), suspendedUntil });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  const owed = balance ? Number(balance.withdrawable_balance) : 0;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'banned' ? 'Ban' : 'Suspend'}{' '}
        {singleUser ? singleUser.name : `${users.length} users`}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
            <FormControlLabel value="suspended" control={<Radio />} label="Suspend (temporary)" />
            <FormControlLabel value="banned" control={<Radio />} label="Ban (permanent)" />
          </RadioGroup>

          <TextField
            label="Reason (shared with the user)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            multiline
            minRows={2}
            fullWidth
            helperText="Included in the notification email — keep it factual."
          />

          {mode === 'suspended' && (
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Duration</InputLabel>
                <Select label="Duration" value={duration} onChange={(e) => setDuration(e.target.value)}>
                  {DURATIONS.map((d) => (
                    <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {duration === 'custom' && (
                <TextField
                  label="Until"
                  type="date"
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              )}
            </Stack>
          )}

          {singleUser && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              {balanceLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">Checking balance…</Typography>
                </Stack>
              ) : owed > 0 ? (
                <>
                  <Typography variant="subtitle2">Owed to this user: ${owed.toFixed(2)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {mode === 'banned'
                      ? 'A payout for the full amount will be created automatically and queued for approval.'
                      : 'Their balance stays intact and is withdrawable once the suspension ends.'}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No outstanding balance owed to this user.
                </Typography>
              )}
            </Box>
          )}

          <Alert severity={mode === 'banned' ? 'error' : 'warning'}>
            {mode === 'banned'
              ? 'Banning is permanent. The user is notified by email with the reason and any payout owed.'
              : 'The user is notified by email with the reason and suspension period.'}
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          disabled={!canConfirm}
          onClick={handleConfirm}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {mode === 'banned' ? 'Ban user' : 'Suspend'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserStatusDialog;
